// Copyright TraderEvolution Global LTD. © 2017-2024. All rights reserved.

import { CustomErrorClass, ErrorInformationStorage } from "../../Commons/ErrorInformationStorage.js"
import { Resources } from "../../Commons/properties/Resources.js"
import { Rectangle } from "../../Commons/Geometry.js"
import { LinkedSystem, LinkedSystemAccLinkingValue } from "../misc/LinkedSystem.js"
import { KeyCode, KeyEventProcessor } from "../../Commons/KeyEventProcessor.js"
import { AdvancedOrderEntryTemplate, OrderEditControlsTemplate, OrderEditParameterHeightTemplate } from "../../templates.js"
import { DockSystemInstance } from "../DockSystem.js"
import { Control } from "../elements/Control.js"
import { TerceraLinkControlConstants } from "../UtilsClasses/TerceraLinkControlConstants.js"
import { TerceraMenu } from "../elements/TerceraMenu.js"
import { OEOrderTypeSelector } from "../trading/OE/OEOrderTypeSelector.js"
import { PanelNames } from "../UtilsClasses/FactoryConstants.js"
import { ApplicationPanelNew } from "./ApplicationPanelNew.js"
import { OrderEditViewBase } from "./OrderEditViewBase.js"
import { OrderType } from "../../Utils/Trading/OrderType.js"
import { PlacedFrom } from "../../Utils/Trading/PlacedFrom.js"
import { RulesSet } from "../../Utils/Rules/RulesSet.js"
import { Account } from "../../Commons/cache/Account.ts"
import { InstrumentSpecificType } from "../../Utils/Instruments/InstrumentSpecificType.js"
import { DynProperty } from "../../Commons/DynProperty.js"
import { GeneralSettings } from "../../Utils/GeneralSettings/GeneralSettings.js"
import { MainWindowManager } from "../UtilsClasses/MainWindowManager.js"
import { Instrument } from "../../Commons/cache/Instrument.js"
import { IsAllowed } from "../../Commons/IsAllowed.js"
import { Quantity } from "../../Utils/Trading/Quantity.js"
import { OrderEditUpdateData } from "../../Utils/Trading/OrderEditUpdateData.js"
import { DataCache } from "../../Commons/DataCache.js"
import { SessionSettings } from "../../Commons/SessionSettings.js"
import { TradingNumericErrorChecker } from "../../Commons/Trading/TradingNumericErrorChecker.js"
import { OrderExecutorUtils } from "../../Commons/Trading/OrderExecutorUtils.js";
import { MathUtils } from "../../Utils/MathUtils.js"

// TODO. Optimize. DynProperty + js properties interface for orderEdit parameters.
// Something like NotificationObserver in MVVM .NET.
// OrderEdit parameter <-> SINGLE reusable DynProperty per Parameter <-> Ractive Control.


export let AdvancedOrderEntry = OrderEditViewBase.extend({
    partials: {
        bodyPartial: AdvancedOrderEntryTemplate,
        orderEditControls: OrderEditControlsTemplate,
        orderEditParameterHeight: OrderEditParameterHeightTemplate
    },
    data: function ()
    {
        return {
            showFooter: false,
            resizable: false,
            width: 340,
            height: 500,
            minWidth: 570,
            dockablePanel: false,
            showHeader: true,
            withBorderRadius: true,
            isAccountLinkShow: true,
            isSymbolLinkShow: true,

            instrument: null,
            account: null,
            quantity: null, // QuantityObject
            side: null,
            tif: null, // TIF object
            orderType: null,
            productType: null,
            productTypeShow: false,
            leverageShow: false,

            disclosedQuantityShow: false,
            disclosedQuantity: null,
            dqMinValue: null,
            dqMaxValue: null,
            dqTooltip: 'panel.newOrderEntry.disclosedLabel.tt',
            dqLessMinLocalKey: 'UserControl.TerceraDQNumeric.ValueLessMin',
            dqGreaterMaxLocalKey: 'UserControl.TerceraDQNumeric.ValueGreaterMax',
            disclosedLabel: '',

            offsetAccount: null,
            offsetDisclosedQuantity: null,
            offsetProductType: null,
            offsetLeverage: null,

            singleAccount: false,
            canFilterByAccount: false,

            selOrderTypeCBItem: null,
            isAlert: false,

            positionSizingVisible: false,
            positionSizingChecked: false,
            delayedVisible: false,
            snapshotVisible: false,
            delayedText: '',
            snapshotText: '',
            snapshotBtnTooltip: '',
            snapshotBtnDisabled: false,

            dataSourceVisible: false,    // true to enable data source
            dataSourceOpen: false,

            marginOEAllowed: false,
            marginOEVisible: false,
            // isHeaderMenuButtonShow: true,
            showAttachHeaderButton: true,
            style_addition_header_button: "js-button-attach",
            attached: false
        }
    },
    headerLocaleKey: 'panel.orderEntry',
    NewParametrsHandler: null,
    closeTimeout: null,
    AutoHideHeaderUpdateHandler: null,
    IsResized: false
})

AdvancedOrderEntry.prototype.updatePanelHeader = function ()
{
    let ins = this.get('instrument')
    let header = Resources.getResource(this.headerLocaleKey) + (ins && !ins.IsEmpty ? (' ' + ins.DisplayName()) : '')
    this.set({ header: header })
    if (this.AutoHideHeaderUpdateHandler)
        this.AutoHideHeaderUpdateHandler(header)
}

AdvancedOrderEntry.prototype.onInstrumentChanged = function (instrument, lastInstrument)
{
    if (!instrument || instrument === lastInstrument || !this.completed)
        return

    this.subscribeRiskSettingsUpdate(instrument, lastInstrument)

    this.instrumentSettingsUpdate()

    this.symbolLink_Out(false, instrument)
    this.updatePanelHeader()
    this.deferLayout()
}

AdvancedOrderEntry.prototype.onAccountChanged = function (account, lastAccount)
{
    if (!account)
        return

    this.instrumentSettingsUpdate() // for productTypeShow and others update

    this.accountLink_Out(false, account);
}

AdvancedOrderEntry.prototype.getType = function ()
{
    return PanelNames.AdvancedOrderEntry
}

AdvancedOrderEntry.prototype.oninit = function ()
{
    OrderEditViewBase.prototype.oninit.call(this)

    this.observe('instrument', this.onInstrumentChanged)
    this.observe('account', this.onAccountChanged)

    this.set(
        'singleAccount',
        DataCache.AccountsCount === 1)

    this.observe(
        'orderType',
        this.onOrderTypeChanged)

    this.observe(
        'instrument account quantity side tif productType disclosedQuantity positionSizingChecked leverageValue',
        this.onTradingDataChanged)

    this.observe('positionSizingChecked', this.updatePositionSizingControls)
    this.observe('instrument account', this.turnOffPositionSizing)

    this.observe(
        'singleAccount disclosedQuantityShow productTypeShow leverageShow',
        this.setTopControlsOffsets
    )

    this.on("placeOrderClick", this.onPlaceOrderClick);
    this.on("btnCancelClick", function () { this.close(); });

    if (!Resources.isHidden('OrderEntry.InfoBlock.Visibility'))
    {
        this.set('width', 350)
        this.set('marginOEAllowed', true)
        this.on('onMarginOEVisibleStateChanged', this.onMarginOEVisibleStateChanged)
    }

    this.set('positionSizingVisible', DataCache.isAllowedForMyUser(RulesSet.FUNCTION_POSITION_SIZING))
    this.observe('delayedVisible snapshotVisible dataSourceVisible', this.deferLayout)
    this.observe('dataSourceOpen', this.onDataSourceOpen)
    DataCache.OnSnapshotResponseReceived.Subscribe(this.updateGetSnapshotBtn, this)     // обновление enability кнопок Get snapshot если на клиенте совершили Get snapshot request 
    Account.TradeStatusChanged.Subscribe(this.updateGetSnapshotBtn, this);               // обновление enability кнопок Get snapshot при изменениях значения на сервере
    this.updateGetSnapshotBtn()

    DataCache.OnUpdateAccount.Subscribe(this.onUpdateAccount, this)

    this.on("attachClick", this.AttachClick);

    this.center()
}

AdvancedOrderEntry.prototype.resizingProcess = function ()
{
    this.IsResized = true;

    if (this.get("marginOEVisible") && this.Controls.marginOEControl)
        this.Controls.marginOEControl.layoutTable();
}

AdvancedOrderEntry.prototype.UpdateAutoHideSate = function (state)
{
    if (this.get("attached") || this.get("isAlert"))
        return;
    this.set("showAttachHeaderButton", state);
}

AdvancedOrderEntry.prototype.ForcedAutoHide = function ()
{
    if (!this.get("attached"))
        return;

    clearTimeout(this.closeTimeout);
    this.set({ visible: false });
    MainWindowManager.MainWindow.resetFocus();
}

AdvancedOrderEntry.prototype.AttachClick = function ()
{
    let attached = this.get("attached");
    if (attached)
    {
        attached = !attached
        clearTimeout(this.closeTimeout);
        this.set({ visible: true, movable: true, attached: attached, resizable: false })

        if (this.get("marginOEVisible") && this.Controls.marginOEControl)
            this.set("resizable", true)

        DockSystemInstance.removeFromAutoHide(this)
    }
    else
    {
        attached = !attached
        this.IsResized = false
        this.set({ visible: false, movable: false, attached: attached, resizable: false })
        DockSystemInstance.addToAutoHide(this);
    }
    this.layout();

    if (!attached)
        this.center();

    MainWindowManager.MainWindow.resetFocus();

    if (this.get("marginOEVisible") && this.Controls.marginOEControl)
        this.Controls.marginOEControl.layoutTable()

    let btnattachText = attached ? Resources.getResource('panel.external.Dettach') : Resources.getResource('panel.external.Attach');
    this.set('header_button_text', btnattachText);
}

AdvancedOrderEntry.prototype.oncomplete = function ()
{
    OrderEditViewBase.prototype.oncomplete.call(this)
    this.set({ showAttachHeaderButton: DockSystemInstance.isAutoHideAvaliable() })
    DockSystemInstance.RegisterPanel(this)

    this.OnResize.Subscribe(this.resizingProcess, this);

    KeyEventProcessor.OnKeyDown.Subscribe(this.onGlobalKeyDown, this)

    if (this.get("isAlert"))
        this.set({ showAttachHeaderButton: false, isAccountLinkShow: false, isSymbolLinkShow: false, canLinkByAccount: false })

    if (this.get("attached"))
    {
        this.set({ visible: false, movable: false })
        DockSystemInstance.addToAutoHide(this);
        MainWindowManager.MainWindow.resetFocus();
    }
    this.onInstrumentChanged(this.get('instrument'), null)

    this.SetFirstElement()
};

AdvancedOrderEntry.prototype.AutoHideHover = function ()
{
    clearTimeout(this.closeTimeout);
    let visible = this.get("visible")
    if (visible)
        return
    this.set({ visible: true })
    this.ChangeAutohideLocation();
}

AdvancedOrderEntry.prototype.onGlobalKeyDown = function (key)
{
    if (this.get('focused'))
        if (key === KeyCode.ESC)
            this.close();
}

AdvancedOrderEntry.prototype.AutoHideLeave = function ()
{
    if (this.get("attached"))
        this.MakeMeHidden()
}

AdvancedOrderEntry.prototype.MakeMeHidden = function ()
{
    clearTimeout(this.closeTimeout);
    this.closeTimeout = setTimeout(function ()
    {
        if (!this.get("focused"))
            this.set({ visible: false })
        clearTimeout(this.closeTimeout);
    }.bind(this), AdvancedOrderEntry.TIME_TO_HIDE);
}
AdvancedOrderEntry.TIME_TO_HIDE = 1000;

AdvancedOrderEntry.prototype.ChangeAutohideLocation = function ()
{
    var newCoord = TerceraMenu.CorrectPopupLocation(new Rectangle(
        2000,
        52,
        this.get("width"),
        this.get("height")))

    this.setLocation(newCoord.newX - 27, newCoord.newY);
}

AdvancedOrderEntry.prototype.lostFocus = function ()
{
    ApplicationPanelNew.prototype.lostFocus.apply(this);
    if (!this.get("attached"))
        return

    if (MainWindowManager.TerceraInstrumentLookupDropDownForm && MainWindowManager.TerceraInstrumentLookupDropDownForm.get("visible"))
    {
        MainWindowManager.TerceraInstrumentLookupDropDownForm.AfterLostFocusWaiting = this;
        return;
    }

    if (MainWindowManager.TerceraAccountLookupDropDownForm && MainWindowManager.TerceraAccountLookupDropDownForm.get("visible"))
    {
        MainWindowManager.TerceraAccountLookupDropDownForm.AfterLostFocusWaiting = this;
        return;
    }
    this.MakeMeHidden()
};

AdvancedOrderEntry.prototype.onMouseEnter = function (event)
{
    this.AutoHideHover();
};

AdvancedOrderEntry.prototype.onMouseLeave = function (event)
{
    this.AutoHideLeave();
};

AdvancedOrderEntry.prototype.gotFocus = function ()
{
    Control.prototype.gotFocus.apply(this);
    clearTimeout(this.closeTimeout);
};

AdvancedOrderEntry.prototype.dispose = function ()
{
    if (this.get("attached"))
        DockSystemInstance.removeFromAutoHide(this)
    DockSystemInstance.UnRegisterPanel(this)

    KeyEventProcessor.OnKeyDown.UnSubscribe(this.onGlobalKeyDown, this)

    DataCache.OnUpdateAccount.UnSubscribe(this.onUpdateAccount, this)
    DataCache.OnSnapshotResponseReceived.UnSubscribe(this.updateGetSnapshotBtn, this)
    Account.TradeStatusChanged.UnSubscribe(this.updateGetSnapshotBtn, this);

    let instrument = this.get('instrument')
    if (instrument)         // TODO! в AdvancedOrderEntry нет метода updateTradingAllowedStuff, заменим на this.instrumentSettingsUpdate ?
        instrument.RiskSettingsUpdated.UnSubscribe(this.updateTradingAllowedStuff, this)

    OrderEditViewBase.prototype.dispose.call(this)
};

AdvancedOrderEntry.prototype.SetFirstElement = function ()
{
    if (this.get('singleAccount'))
        this.Controls.OEInstrumentLookup.setFocus()
    else
        this.Controls.OEAccountLookup.setFocus()
}

AdvancedOrderEntry.prototype.onPlaceOrderClick = function ()
{
    if (this.get("isAlert"))
        this.saveOrderParam();
    else
    {
        if (TradingNumericErrorChecker.HasErrors(this))
            return

        this.placeOrder(this.orderEdit)
    }
}

AdvancedOrderEntry.prototype.selectOrderType = function (newOrderType)
{
    let orderTypeDict = OrderExecutorUtils.getAllowedOrderTypeDict(this.get('account'), this.get('instrument'), null, GeneralSettings.TradingDefaults.OrderType, false)

    let orderType = orderTypeDict[newOrderType] !== undefined ? orderTypeDict[newOrderType] : orderTypeDict[OrderType.Market]

    let selectedItem = { value: orderType, text: Resources.getResource(orderType.localizationKey()) }

    this.set('selOrderTypeCBItem', selectedItem)
}

AdvancedOrderEntry.prototype.onOrderTypeChanged = function (newOrderType, oldOrderType)
{
    if (!newOrderType || newOrderType === oldOrderType)
        return

    let orderEdit = newOrderType.createOrderEditObject({
        dataCache: DataCache
    })

    this.setOrderEdit(orderEdit)

    this.addDisclosedQuantityIfNeed()

    orderEdit.updateParameters(new OrderEditUpdateData(
        null,
        this.getAllTradingDataDict()))

    this.updatePositionSizingControls()
}

// TODO. Rename; Parameters' constants.
AdvancedOrderEntry.prototype.onTradingDataChanged = function (newVal, oldVal, key)
{
    let orderEdit = this.orderEdit
    if (!orderEdit) return

    let tradingDataDict = {}
    tradingDataDict[key] = newVal

    tradingDataDict.completed = this.completed

    this.addDisclosedQuantityIfNeed(key)

    orderEdit.updateParameters(new OrderEditUpdateData(
        null,
        tradingDataDict))

    this.deferLayout()
}

// TODO. Rename; Parameters' constants.
AdvancedOrderEntry.prototype.getAllTradingDataDict = function ()
{
    let tradingDataDict = {}

    let instrument = this.get('instrument')
    if (instrument) tradingDataDict.instrument = instrument

    let account = this.get('account')
    if (account) tradingDataDict.account = account

    let quantity = this.get('quantity')
    if (quantity) tradingDataDict.quantity = quantity

    let tif = this.get('tif')
    if (tif) tradingDataDict.tif = tif

    let side = this.get('side')
    if (side !== null) tradingDataDict.side = side

    let productType = this.get('productType')
    if (productType !== null) tradingDataDict.productType = productType

    let leverageValue = this.get('leverageValue')
    if (leverageValue) tradingDataDict.leverageValue = leverageValue

    let disclosedQuantity = this.get('disclosedQuantity')
    if (disclosedQuantity !== null) tradingDataDict.disclosedQuantity = disclosedQuantity

    let positionSizingChecked = this.get('positionSizingChecked')
    if (positionSizingChecked !== null) tradingDataDict.positionSizingChecked = positionSizingChecked

    tradingDataDict.completed = this.completed

    return tradingDataDict
}

AdvancedOrderEntry.prototype.layout = function ()
{
    let topPartH = this.get('singleAccount') ? 176 : 207

    if (this.get('disclosedQuantityShow'))
        topPartH += 31

    if (this.get('productTypeShow'))
        topPartH += 31

    if (this.get('leverageShow'))
        topPartH += 31

    let orderEditPartH = this.getOrderEditParametersHeight()
    let bottomPartH = 111

    let OEQuote1ViewerTop = orderEditPartH + topPartH - 10;
    let dataSourceWidgetTop = OEQuote1ViewerTop + 69;

    if (this.get('delayedVisible') || this.get('snapshotVisible'))
    {
        bottomPartH += 26
        dataSourceWidgetTop += 26
    }

    let OEPlaceOrderButtonTop = dataSourceWidgetTop - 10;
    if (this.get('dataSourceVisible'))
    {
        bottomPartH += 25
        OEPlaceOrderButtonTop += 25

        if (this.get('dataSourceOpen'))
        {
            bottomPartH += 78
            OEPlaceOrderButtonTop += 78
        }
    }

    this.set({
        OEQuote1ViewerTop: OEQuote1ViewerTop,
        dataSourceWidgetTop: dataSourceWidgetTop,
        OEPlaceOrderButtonTop: OEPlaceOrderButtonTop,
    })

    if (this.get("attached"))
        this.set('height', MainWindowManager.MainWindow.GetAvaliableHeight() - 2)
    else
    {
        this.set('minHeight', topPartH + orderEditPartH + bottomPartH)

        let needResize = (!this.get("resizable") || (this.get("height") < this.get("minHeight")) || !this.IsResized);
        if (needResize)
            this.set('height', topPartH + orderEditPartH + bottomPartH)
    }

    if (this.get("marginOEVisible") && this.Controls.marginOEControl)
        this.Controls.marginOEControl.layoutTable()
}

AdvancedOrderEntry.prototype.saveOrderParam = function ()
{
    if (this.NewParametrsHandler)
        this.NewParametrsHandler(this.orderEdit)
    this.close();
}

AdvancedOrderEntry.prototype.placeOrder = function (orderEdit)
{
    orderEdit.placedFrom = PlacedFrom.WEB_OE;
    DataCache.FOrderExecutor.placeOrderPromise(orderEdit, null, null, this.focusWarningNumeric.bind(this)).catch(function () 
    {
        let ex = new CustomErrorClass("AdvancedOrderEntry error", "AdvancedOrderEntry.placeOrder", "placeOrder -> placeOrderPromise");
        ErrorInformationStorage.GetException(ex);
    })
}

AdvancedOrderEntry.prototype.updateSettings = function ()
{
    let ins = this.get('instrument'), acc = this.get('account')
    if (!ins || !acc)
        return
    this.updateTradingAllowedAndReasonTooltip()
}


//#region ICaller

AdvancedOrderEntry.prototype.callBack = function (properties)
{
    ApplicationPanelNew.prototype.callBack.call(this, properties);

    this.set('instrument', this.getCallBackInstrument(properties, 'symbol'));

    let dp = DynProperty.getPropertyByName(properties, 'account');
    if (dp && dp.value) this.set('account', SessionSettings.getDefValueFromObj(DataCache.Accounts[dp.value], DataCache.Accounts));

    dp = DynProperty.getPropertyByName(properties, 'marginOEVisible');
    if (dp) this.set('marginOEVisible', dp.value);

    dp = DynProperty.getPropertyByName(properties, 'dataSourceOpen');
    if (dp) this.set('dataSourceOpen', dp.value);

    dp = DynProperty.getPropertyByName(properties, 'resizable');
    if (dp) this.set('resizable', dp.value);

    let attached = DynProperty.getPropValue(properties, "attached");
    this.set({ attached: attached })
};

AdvancedOrderEntry.prototype.Properties = function ()
{
    var properties = ApplicationPanelNew.prototype.Properties.call(this);
    var ins = this.get('instrument');
    if (ins) properties.push(new DynProperty("symbol", ins.GetInteriorID(), DynProperty.STRING, DynProperty.HIDDEN_GROUP));

    var acc = this.get('account');
    if (acc) properties.push(new DynProperty("account", acc.AcctNumber, DynProperty.STRING, DynProperty.HIDDEN_GROUP));

    var marginOEVisible = this.get('marginOEVisible');
    properties.push(new DynProperty("marginOEVisible", marginOEVisible, DynProperty.BOOLEAN, DynProperty.HIDDEN_GROUP));

    var resizable = this.get('resizable');
    properties.push(new DynProperty("resizable", resizable, DynProperty.BOOLEAN, DynProperty.HIDDEN_GROUP));

    var dataSourceOpen = this.get('dataSourceOpen');
    properties.push(new DynProperty("dataSourceOpen", dataSourceOpen, DynProperty.BOOLEAN, DynProperty.HIDDEN_GROUP));

    properties.push(new DynProperty("attached", this.get("attached"), DynProperty.BOOLEAN, DynProperty.HIDDEN_GROUP));

    return properties;
};

//#endregion

//#region Link
AdvancedOrderEntry.prototype.accountLink_Out = function (newSubscriber, account)
{
    if (!account)
    {
        let acc = this.get('account')
        if (!acc) return
        account = acc
    }

    if (LinkedSystem.accLinkingActive)
        this.completed ?
            LinkedSystem.setAccount(LinkedSystemAccLinkingValue, account.BstrAccount, newSubscriber) :
            this.accountLink_In(LinkedSystem.getAccount(LinkedSystemAccLinkingValue))
}

AdvancedOrderEntry.prototype.symbolLink_Out = function (newSubscriber, instrument)
{
    if (!instrument)
    {
        let ins = this.get('instrument')
        if (!ins) return
        instrument = ins
    }

    let color = this.get('symbolLinkValue')
    if (color !== TerceraLinkControlConstants.STATE_NONE)
        LinkedSystem.setSymbol(color, instrument.GetInteriorID(), newSubscriber)
}

AdvancedOrderEntry.prototype.accountLink_In = function (accId)
{
    if (DataCache.Accounts[accId])
        this.set('account', DataCache.Accounts[accId])

    this.localizeAccountLinkTooltip()
}

AdvancedOrderEntry.prototype.symbolLink_In = function (symbolName)
{
    let newInstr = DataCache.getInstrumentByName(symbolName)
    if (newInstr)
        this.set('instrument', newInstr)
}

//#endregion Link

AdvancedOrderEntry.prototype.setTopControlsOffsets = function ()
{
    let offsetAccount = 9
    if (!this.get('singleAccount'))
        offsetAccount += 31

    let offsetDisclosedQuantity = offsetAccount
    if (this.get('disclosedQuantityShow'))
        offsetDisclosedQuantity += 31

    let offsetProductType = offsetDisclosedQuantity
    if (this.get('productTypeShow'))
        offsetProductType += 31

    let offsetLeverage = offsetProductType
    if (this.get('leverageShow'))
        offsetLeverage += 31

    this.set({
        offsetAccount: offsetAccount,
        offsetDisclosedQuantity: offsetDisclosedQuantity,
        offsetProductType: offsetProductType,
        offsetLeverage: offsetLeverage
    })
}

AdvancedOrderEntry.prototype.localize = function ()
{
    OrderEditViewBase.prototype.localize.call(this);
    this.set('disclosedLabel', Resources.getResource('panel.newOrderEntry.disclosedLabel'))
    this.set('buttonCancelText', Resources.getResource('general.messageBox.cancel'))

    this.set('delayedText', Resources.getResource('panel.newOrderEntry.delayedText'))
    this.set('snapshotText', Resources.getResource('panel.newOrderEntry.snapshotText'));
    let btnattachText = this.get("attached") ? Resources.getResource('panel.external.Dettach') : Resources.getResource('panel.external.Attach');
    this.set('header_button_text', btnattachText);
    this.updateTooltipForSnapshotBtn()
};

AdvancedOrderEntry.prototype.onUpdateAccount = function (account)
{
    let orderEdit = this.orderEdit
    if (!orderEdit) return

    orderEdit.onUpdateAccount(account)
}

AdvancedOrderEntry.prototype.instrumentSettingsUpdate = function ()
{
    let instrument = this.get('instrument'),
        productType = this.get('productType'),
        account = this.get('account');

    if (!Instrument.IsWorkingInstrument(instrument))
        return

    if (account)
        this.updateTradingAllowedAndReasonTooltip()

    this.set('productTypeShow', instrument.isProductTypeVisible(account))
    this.set('leverageShow', instrument.isLeverageVisible(account, productType))

    this.updateDelayedAndSnapshotVisibility()
}

AdvancedOrderEntry.prototype.updateTradingAllowedAndReasonTooltip = function ()
{
    let instrument = this.get('instrument'),
        account = this.get('account');

    let ordertype = this.get("orderType")
    let ordT = -1;
    if (ordertype)
        ordT = this.get("orderType").id()

    let tradingAllowed = IsAllowed.IsTradingAllowed([account], instrument, ordT),
        tradingForbiddenReason = tradingAllowed.ReasonText;

    if (tradingAllowed.Allowed && ordT === -1)
    {
        tradingForbiddenReason = Resources.getResource("Not selected order type");
        tradingAllowed.Allowed = false;
    }

    this.set({
        isAllowedResponce: tradingAllowed,
        tradingAllowed: tradingAllowed.Allowed,
        tradingForbiddenReason: tradingForbiddenReason
    })
}

AdvancedOrderEntry.prototype.subscribeRiskSettingsUpdate = function (instrument, lastInstrument)
{
    if (lastInstrument)
        lastInstrument.RiskSettingsUpdated.UnSubscribe(this.instrumentSettingsUpdate, this)

    if (instrument && !instrument.IsEmpty)
        instrument.RiskSettingsUpdated.Subscribe(this.instrumentSettingsUpdate, this)
}

AdvancedOrderEntry.prototype.onMarginOEVisibleStateChanged = function ()
{
    let oldState = this.get('marginOEVisible'),
        newState = !oldState

    this.set('marginOEVisible', newState)
    this.set('width', newState ? 684 : 350)

    if (!this.get("attached"))
        this.set('resizable', newState)

    if (!newState)
    {
        this.IsResized = false;
        this.layout();
    }

    if (this.get("attached"))
        this.ChangeAutohideLocation();
}

AdvancedOrderEntry.prototype.onQuantityChanged = function (qtyValue)
{
    let orderEdit = this.orderEdit
    if (!orderEdit) return

    let ins = orderEdit.instrument
    if (!ins) return

    let OEQty = this.Controls ? this.Controls.OEQuantity : null,
        acc = orderEdit.account, pt = orderEdit.productType,
        isAllowedResponce = this.get('isAllowedResponce')

    if (isAllowedResponce && isAllowedResponce.Allowed)     // доп валидацию по qty посчитанную position sizing-ом можно применить если торговля разрешена в IsAllowed.IsTradingAllowed 
    {
        let inLots = orderEdit.sessionSettings ? GeneralSettings.View.displayAmountInLots() : null,
            minLot = Quantity.convertQuantityValue(new Quantity(ins.getMinLot(pt, acc), true), ins, inLots),
            maxLot = Quantity.convertQuantityValue(new Quantity(ins.getMaxLot(pt, acc), true), ins, inLots)

        let tradingAllowed = qtyValue >= minLot && qtyValue <= maxLot,
            reasonLocKey = tradingAllowed ? '' :
                ('positionSizeCalculator.OEButtonDisabled.' + (qtyValue < minLot ? 'less' : 'more'))

        let reasonText = Resources.getResource(reasonLocKey)
        if (!tradingAllowed)
            reasonText = reasonText.replace('{0}', qtyValue < minLot ? minLot : maxLot)

        this.set({
            tradingAllowed: tradingAllowed,
            tradingForbiddenReason: reasonText
        })
    }

    if (OEQty)
    {
        OEQty.set('value', qtyValue)
        OEQty.updateQuantity()///// TODO !!!!! TODO !!!!!!!!!!!!!!!!!!!!!!!!!
    }

    if (this.get('positionSizingChecked'))
        this.flushQuantity()
}

AdvancedOrderEntry.prototype.flushQuantity = function ()
{
    let oe = this

    oe.set('qtyCustomCSSClass', 'js-flushed-qty-style')

    if (oe.flushQtyTimer)
        clearTimeout(oe.flushQtyTimer)

    oe.flushQtyTimer = setTimeout(function () { oe.set('qtyCustomCSSClass', '') }, 1000)
}

AdvancedOrderEntry.prototype.updatePositionSizingControls = function ()  // обновить подсветку лейблов, участвующих в positionSizing-e, включить SL
{
    if (!this.orderEdit) return

    let checked = this.get('positionSizingChecked')

    setTimeout(function ()
    {
        if (!this.orderEdit) return

        if (checked)
            this.enableSL()     // при смене orderEdit сбрасывается enable sl-а, который при включенном positionSizing должен быть всегда 

        this.orderEdit.customCSS = checked ? 'js-flushed-labels-for-position-sizing' : ''

        let paramArray = this.get('orderEditParameterArray')
        for (let i = 0; i < paramArray.length; i++)
        {
            if (!this.orderEdit.skipParamForFlashing(paramArray[i].name))
                paramArray[i].customCSS = checked ? 'js-flushed-labels-for-position-sizing' : ''
        }

        this.set('orderEditParameterArray', paramArray)

    }.bind(this), 1)
}

AdvancedOrderEntry.prototype.turnOffPositionSizing = function (newData, oldData)    //#100580
{
    if (!newData || !oldData)
        return

    if (newData.GetInteriorID && newData.GetInteriorID() == oldData.GetInteriorID())// инструменты  
        return // #100615 коммент

    if (newData.AcctNumber && newData.AcctNumber == oldData.AcctNumber)             // аккаунты
        return

    this.set('positionSizingChecked', false)
}

AdvancedOrderEntry.prototype.enableSL = function ()
{
    let orderEdit = this.orderEdit
    if (!orderEdit || !orderEdit.enableSL) return
    orderEdit.enableSL()
}
AdvancedOrderEntry.prototype.updateDelayedAndSnapshotVisibility = function ()
{
    let instrument = this.get('instrument')
    if (!instrument) return

    if (instrument.InstrumentSpecificType === InstrumentSpecificType.ContiniousContract)
        instrument = DataCache.getInstrument(instrument.InstrumentTradableID.toString(), instrument.Route);   // #100762

    let canGetSnapshot = instrument.CanGetSnapshot(),
        delayMoreThanZero = instrument.GetDelay() > 0,
        subsribeErrorCode = instrument.HasLvl1SubscriptionErrorCode,
        delayedVisible = delayMoreThanZero,         // 5), 6), 7), 8)
        snapshotVisible = canGetSnapshot            // 1), 2), 3), 4), 6), 7), 8)

    // 1) delay > 0 && snapshot == TRUE && error code = true  	- ПОКАЗЫВАТЬ КНОПКУ, НЕ ПОКАЗЫВАТЬ DELAYED DATA - не реальный кейс
    // 2) delay > 0 && snapshot == TRUE &&  error code = false  	- ПОКАЗЫВАТЬ КНОПКУ И DELAYED DATA 							
    // 3) delay > 0 && snapshot == FALSE && error code = false   	- НЕ ПОКАЗЫВАТЬ КНОПКУ И DELAYED DATA						
    // 4) delay > 0 && snapshot == FALSE &&  error code = true  	- НЕ ПОКАЗЫВАТЬ КНОПКУ И DELAYED DATA						

    // 5) delay = 0 && snapshot == TRUE && error code = true   	- ПОКАЗЫВАТЬ КНОПКУ, НЕ ПОКАЗЫВАТЬ DELAYED DATA				
    // 6) delay = 0 && snapshot == TRUE && error code = false 		- НЕ ПОКАЗЫВАТЬ КНОПКУ И DELAYED DATA						
    // 7) delay = 0 && snapshot == FALSE && error code = false      - НЕ ПОКАЗЫВАТЬ КНОПКУ И DELAYED DATA 						
    // 8) delay = 0 && snapshot == FALSE && error code = true    	- НЕ ПОКАЗЫВАТЬ КНОПКУ И DELAYED DATA

    if (delayMoreThanZero)
    {
        if (canGetSnapshot)
            delayedVisible = !subsribeErrorCode      // 1), 2)
        else
            delayedVisible = false                   // 3), 4)
    }
    else if (canGetSnapshot)
        snapshotVisible = subsribeErrorCode          // 5)

    this.set({
        snapshotVisible: snapshotVisible,
        delayedVisible: delayedVisible
    })
}

AdvancedOrderEntry.prototype.updateGetSnapshotBtn = function ()
{
    this.updateSnapshotBtnEnability()
    this.updateTooltipForSnapshotBtn()
}

AdvancedOrderEntry.prototype.updateTooltipForSnapshotBtn = function ()
{
    let snapshotBtnDisabled = AdvancedOrderEntry.isGetSnapshotBtnDisabled(),
        tt = Resources.getResource('panel.newOrderEntry.snapshotBtn.' + (snapshotBtnDisabled ? 'disabled' : 'enabled') + '.tt')

    this.set('snapshotBtnTooltip', tt)
}

AdvancedOrderEntry.prototype.updateSnapshotBtnEnability = function ()
{
    this.set('snapshotBtnDisabled', AdvancedOrderEntry.isGetSnapshotBtnDisabled())
}

AdvancedOrderEntry.isGetSnapshotBtnDisabled = function () 
{
    return DataCache.getAvailableNumberOfSnapshotRequests() === 0
}

AdvancedOrderEntry.prototype.onDataSourceOpen = function () 
{
    this.deferLayout()

    let marginOE = this.Controls.marginOEControl
    if (marginOE)
    {
        let updateMarginCaller = marginOE.UpdateTable.bind(marginOE)
        setTimeout(updateMarginCaller, 100)
    }
}

AdvancedOrderEntry.prototype.fillByOrderEdit = function (orderEdit) {
    if (MathUtils.IsNullOrUndefined(orderEdit)) {
        return;
    }

    this.set({
        instrument: orderEdit.instrument,
        account: orderEdit.account,
    }).then(() => {
        this.selectOrderType(orderEdit.getOrderTypeId());
        this.setOrderEdit(orderEdit);
        this.set({side: orderEdit.side});
        this.set({defaultTif: orderEdit.tif});
        this.set({productType: orderEdit.productType});
        this.set({defaultQuantity: orderEdit.quantity});
        this.set({disclosedQuantity: orderEdit.disclosedQuantity});
        this.set({leverageValue: orderEdit.leverageValue});
    });
}